import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook to manage badge celebrations
 * - Checks for newly awarded badges on mount
 * - Shows celebration modals for badges not yet seen
 * - Marks badges as "celebration_shown" after displaying
 */
export function useBadgeCelebration(userId) {
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [currentBadge, setCurrentBadge] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check for uncelebrated badges
  const checkForNewBadges = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      // Fetch user badges that haven't been celebrated yet
      const { data: userBadges, error } = await supabase
        .from('user_badges')
        .select(`
          id,
          badge_id,
          earned_at,
          celebration_shown,
          badges (
            id,
            name,
            description,
            icon
          )
        `)
        .eq('user_id', userId)
        .eq('celebration_shown', false);

      if (error) {
        // If column doesn't exist yet, fail silently
        if (error.message.includes('celebration_shown')) {
          console.log('celebration_shown column not yet added');
          return;
        }
        console.error('Error fetching uncelebrated badges:', error);
        return;
      }

      if (userBadges && userBadges.length > 0) {
        // Transform badges for celebration queue
        const badgesToCelebrate = userBadges.map(ub => ({
          userBadgeId: ub.id,
          ...ub.badges,
          earned_at: ub.earned_at
        }));
        
        setCelebrationQueue(badgesToCelebrate);
        // Show the first badge
        setCurrentBadge(badgesToCelebrate[0]);
      }
    } catch (err) {
      console.error('Error checking for new badges:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Check and award badges (retroactive check)
  const checkAndAwardBadges = useCallback(async () => {
    if (!userId) return;
    
    try {
      await supabase.rpc('check_and_award_badges', { p_user_id: userId });
      // After awarding, check for uncelebrated badges
      await checkForNewBadges();
    } catch (err) {
      // Function might not exist yet
      console.log('Badge check skipped:', err.message);
    }
  }, [userId, checkForNewBadges]);

  // Mark badge celebration as shown and move to next
  const dismissCelebration = useCallback(async () => {
    if (!currentBadge) return;
    
    try {
      // Mark as shown in database using RPC to bypass RLS
      const { error } = await supabase.rpc('mark_badge_celebration_shown', {
        p_user_badge_id: currentBadge.userBadgeId
      });
      
      if (error) {
        console.error('Error marking celebration as shown:', error);
        // Fallback: try direct update (might work if RLS allows it)
        await supabase
          .from('user_badges')
          .update({ celebration_shown: true })
          .eq('id', currentBadge.userBadgeId);
      }
    } catch (err) {
      console.error('Error marking celebration as shown:', err);
    }

    // Move to next badge in queue
    setCelebrationQueue(prev => {
      const remaining = prev.slice(1);
      if (remaining.length > 0) {
        setCurrentBadge(remaining[0]);
      } else {
        setCurrentBadge(null);
      }
      return remaining;
    });
  }, [currentBadge]);

  // Run check on mount and when userId changes
  useEffect(() => {
    if (userId) {
      // First check and award any retroactive badges
      checkAndAwardBadges();
    }
  }, [userId, checkAndAwardBadges]);

  // Manually trigger a badge celebration (for immediate feedback after action)
  const triggerBadgeCheck = useCallback(async () => {
    await checkAndAwardBadges();
  }, [checkAndAwardBadges]);

  return {
    currentBadge,
    celebrationQueue,
    dismissCelebration,
    triggerBadgeCheck,
    hasCelebrations: celebrationQueue.length > 0,
    loading
  };
}

export default useBadgeCelebration;

